/**
 * Host review of phone answers, one round at a time: /events/:id/answers.
 *
 * Each question shows the answer key and every team's phone answer with an
 * auto mark. The host can change any mark with one click (correct, half,
 * wrong). "Save marks" writes the same score rows the scoring grid writes,
 * so standings, doubling, and results work as before. New answers appear
 * live (socket "answers-changed" signal, then a reload).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Badge, Button, Callout, Card, Flex, Heading, SegmentedControl, Spinner, Text } from '@radix-ui/themes';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useRoomSignals } from '../../utils/useRealtime';
import RoundLocks from './RoundLocks';
import { choiceLetter } from '../../utils/multipleChoice';
import './AnswerReview.css';

type Mark = 'full' | 'half' | 'wrong';

interface Entry {
  teamId: string;
  teamName: string;
  // Multiple choice: the 0-based index of the tapped choice, or null.
  choiceIndex?: number | null;
  partA: string;
  partB: string | null;
  auto: Mark;
  close: boolean;
  saved: Mark | null;
}

interface ReviewQuestion {
  id: string;
  number: number;
  type: 'TEXT' | 'SONG' | 'IMAGE';
  text: string;
  answer: string;
  answerPartB: string | null;
  acceptableAnswers: string[];
  songTitle: string | null;
  songArtist: string | null;
  // Multiple choice: the choice texts and the correct index.
  choices?: string[];
  correctChoice?: number | null;
  // Value of this question (own value or the round value).
  points?: number | null;
  entries: Entry[];
}

interface ReviewData {
  round: { roundNumber: number; title: string; roundType: 'STANDARD' | 'VISUAL' | 'AUDIO'; twoAnswerMode: boolean };
  rounds: { roundNumber: number; title: string; roundType: string; locked: boolean }[];
  answerMode: 'PAPER' | 'PHONE' | 'MIXED';
  locked: boolean;
  questions: ReviewQuestion[];
  visualSaved: Record<string, number>;
}

const key = (teamId: string, questionId: string) => `${teamId}:${questionId}`;

export default function AnswerReview() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const roundNumber = Math.max(1, parseInt(params.get('round') || '1', 10) || 1);

  const [data, setData] = useState<ReviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Host overrides not saved yet, keyed team:question. Kept across live
  // reloads so a new answer arriving does not wipe the host's clicks.
  const [overrides, setOverrides] = useState<Record<string, Mark>>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get(`/api/answers/event/${id}/round/${roundNumber}`);
      setData(res.data.data);
      setError(null);
    } catch {
      setError('Could not load phone answers for this round.');
    } finally {
      setLoading(false);
    }
  }, [id, roundNumber]);

  useEffect(() => {
    setLoading(true);
    setOverrides({});
    load();
  }, [load]);

  // Reload at most every 1.5 s while answers stream in.
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleReload = useCallback(() => {
    if (reloadTimer.current) return;
    reloadTimer.current = setTimeout(() => {
      reloadTimer.current = null;
      load();
    }, 1500);
  }, [load]);
  useEffect(() => () => { if (reloadTimer.current) clearTimeout(reloadTimer.current); }, []);

  useRoomSignals(
    id ? { event: 'join-host', eventId: id } : null,
    {
      'answers-changed': (p) => {
        const rounds = (p as { rounds?: number[] })?.rounds ?? [];
        if (rounds.length === 0 || rounds.includes(roundNumber)) scheduleReload();
      },
      'teams-changed': scheduleReload,
    },
    load,
  );

  const current = useCallback(
    (q: ReviewQuestion, e: Entry): Mark => overrides[key(e.teamId, q.id)] ?? e.saved ?? e.auto,
    [overrides],
  );

  const unsaved = useMemo(() => {
    if (!data) return 0;
    let n = 0;
    for (const q of data.questions) {
      for (const e of q.entries) {
        if (data.round.roundType === 'VISUAL') {
          if (overrides[key(e.teamId, q.id)]) n++;
        } else if (e.saved !== current(q, e)) n++;
      }
    }
    return n;
  }, [data, overrides, current]);

  // VISUAL: per-team total of items marked correct, and the saved total.
  const visualTotals = useMemo(() => {
    const out = new Map<string, { name: string; total: number }>();
    if (!data || data.round.roundType !== 'VISUAL') return out;
    for (const q of data.questions) {
      for (const e of q.entries) {
        const row = out.get(e.teamId) ?? { name: e.teamName, total: 0 };
        if (current(q, e) === 'full') row.total += 1;
        out.set(e.teamId, row);
      }
    }
    return out;
  }, [data, current]);

  const setMark = (q: ReviewQuestion, e: Entry, m: Mark) =>
    setOverrides((o) => ({ ...o, [key(e.teamId, q.id)]: m }));

  const acceptAllAuto = () => {
    if (!data) return;
    const next: Record<string, Mark> = {};
    for (const q of data.questions) for (const e of q.entries) next[key(e.teamId, q.id)] = e.auto;
    setOverrides(next);
  };

  const save = async () => {
    if (!data || !id) return;
    const marks = data.questions.flatMap((q) =>
      q.entries.map((e) => ({ teamId: e.teamId, questionId: q.id, mark: current(q, e) })),
    );
    if (marks.length === 0) return;
    setSaving(true);
    try {
      await api.post(`/api/answers/event/${id}/round/${roundNumber}/save`, { marks });
      toast.success(`Saved round ${roundNumber} marks`);
      setOverrides({});
      await load();
    } catch {
      toast.error('Could not save the marks');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return <Flex p="6" gap="2" align="center"><Spinner /> <Text>Loading phone answers…</Text></Flex>;
  }
  if (error || !data) {
    return (
      <Callout.Root color="red" m="4"><Callout.Text>{error || 'Not found'}</Callout.Text></Callout.Root>
    );
  }

  const answerCount = data.questions.reduce((n, q) => n + q.entries.length, 0);

  return (
    <div className="ar-page">
      <Flex align="center" gap="3" wrap="wrap" mb="3">
        <Link to={`/events/${id}/hub`} className="ar-back">&larr; Game Night</Link>
        <Heading size="5" className="ar-title">Phone answers</Heading>
        <Button asChild size="1" variant="outline"><Link to={`/events/${id}/score`}>Scoring</Link></Button>
      </Flex>

      {data.answerMode === 'PAPER' && (
        <Callout.Root color="amber" mb="3">
          <Callout.Text>Phone answers are off for this game night. Turn them on in the Game Night hub.</Callout.Text>
        </Callout.Root>
      )}

      <div className="ar-rounds">
        <SegmentedControl.Root value={String(roundNumber)} onValueChange={(v) => setParams({ round: v })}>
          {data.rounds.map((r) => (
            <SegmentedControl.Item key={r.roundNumber} value={String(r.roundNumber)}>
              R{r.roundNumber}{r.roundType === 'VISUAL' ? ' Vis' : r.roundType === 'AUDIO' ? ' Aud' : ''}
              {r.locked ? ' (closed)' : ''}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl.Root>
      </div>

      <Card size="2" my="3">
        <Flex justify="between" align="center" gap="3" wrap="wrap">
          <Flex direction="column">
            <Text weight="bold">Round {data.round.roundNumber}: {data.round.title}</Text>
            <Text size="2" color="gray">{answerCount} phone answers</Text>
          </Flex>
          <Badge color={data.locked ? 'gray' : 'green'} size="2">
            {data.locked ? 'Closed to phones' : 'Open to phones'}
          </Badge>
          <Flex gap="2">
            <Button variant="soft" color="gray" onClick={acceptAllAuto} disabled={answerCount === 0}>
              Use all auto marks
            </Button>
            <Button onClick={save} disabled={saving || answerCount === 0}>
              {saving ? 'Saving…' : unsaved > 0 ? `Save marks (${unsaved})` : 'Save marks'}
            </Button>
          </Flex>
        </Flex>
      </Card>

      {id && data.answerMode !== 'PAPER' && (
        <div className="ar-locks">
          <RoundLocks
            eventId={id}
            current={roundNumber}
            onChange={(lockedRounds) =>
              setData((d) =>
                d
                  ? {
                      ...d,
                      locked: lockedRounds.includes(roundNumber),
                      rounds: d.rounds.map((r) => ({ ...r, locked: lockedRounds.includes(r.roundNumber) })),
                    }
                  : d,
              )
            }
          />
        </div>
      )}

      {data.round.roundType === 'VISUAL' && visualTotals.size > 0 && (
        <Card size="1" mb="3">
          <Text size="2" weight="bold">Visual totals (1 point per correct item)</Text>
          <div className="ar-visual-totals">
            {[...visualTotals.entries()].map(([teamId, v]) => (
              <span key={teamId} className="ar-visual-chip">
                {v.name}: <b>{v.total}</b>
                {data.visualSaved[teamId] != null && data.visualSaved[teamId] !== v.total && (
                  <Text size="1" color="amber"> (saved {data.visualSaved[teamId]})</Text>
                )}
              </span>
            ))}
          </div>
        </Card>
      )}

      <Flex direction="column" gap="3">
        {data.questions.map((q) => (
          <Card key={q.id} size="2">
            <Flex direction="column" gap="1" mb="2">
              <Text weight="bold">
                Q{q.number}. {q.type === 'SONG' ? <Badge color="violet" size="1" mr="1">Song</Badge> : null}
                {q.text}
              </Text>
              <Text size="2" color="green">
                Key: {keyText(q, data.round)}
                {q.acceptableAnswers.length > 0 && (
                  <Text color="gray"> (also: {q.acceptableAnswers.join(', ')})</Text>
                )}
              </Text>
            </Flex>
            {q.entries.length === 0 ? (
              <Text size="2" color="gray">No phone answers yet.</Text>
            ) : (
              <div className="ar-entries">
                {q.entries.map((e) => {
                  const m = current(q, e);
                  const changed = overrides[key(e.teamId, q.id)] !== undefined;
                  return (
                    <div key={e.teamId} className="ar-entry">
                      <div className="ar-entry-main">
                        <Text size="2" weight="bold" className="ar-team">{e.teamName}</Text>
                        <Text size="2" className="ar-answer">
                          {isChoice(q) ? (
                            e.choiceIndex != null ? (
                              <>
                                <b>{choiceLetter(e.choiceIndex)}</b>
                                {q.choices![e.choiceIndex] ? <> · {q.choices![e.choiceIndex]}</> : null}
                              </>
                            ) : (
                              e.partA || <em>(blank)</em>
                            )
                          ) : (
                            <>
                              {e.partA || <em>(blank)</em>}
                              {e.partB != null && e.partB !== '' && <> / {e.partB}</>}
                            </>
                          )}
                        </Text>
                        <Flex gap="1" wrap="wrap">
                          <Badge color={e.auto === 'full' ? 'green' : e.auto === 'half' ? 'amber' : 'gray'} size="1">
                            auto {symbol(e.auto)}
                          </Badge>
                          {e.close && e.auto !== 'full' && <Badge color="amber" size="1">close</Badge>}
                          {e.saved && !changed && <Badge color="blue" size="1">saved</Badge>}
                        </Flex>
                      </div>
                      <div className="ar-marks" role="group" aria-label={`Mark for ${e.teamName}`}>
                        {(['full', 'half', 'wrong'] as Mark[])
                          .filter((opt) => opt !== 'half' || data.round.roundType !== 'VISUAL')
                          .map((opt) => (
                            <Button
                              key={opt}
                              size="2"
                              variant={m === opt ? 'solid' : 'soft'}
                              color={opt === 'full' ? 'green' : opt === 'half' ? 'amber' : 'red'}
                              aria-pressed={m === opt}
                              aria-label={opt === 'full' ? 'Correct' : opt === 'half' ? 'Half credit' : 'Wrong'}
                              onClick={() => setMark(q, e, opt)}
                            >
                              {symbol(opt)}
                            </Button>
                          ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        ))}

      </Flex>
    </div>
  );
}

function symbol(m: Mark): string {
  return m === 'full' ? '✓' : m === 'half' ? '½' : '✗';
}

function isChoice(q: ReviewQuestion): boolean {
  return Array.isArray(q.choices) && q.choices.length >= 2;
}

function keyText(q: ReviewQuestion, round: ReviewData['round']): string {
  // Multiple choice: the letter and the text of the correct choice.
  if (isChoice(q)) {
    if (q.correctChoice == null) return 'No correct choice set. Mark it in the quiz editor.';
    return `${choiceLetter(q.correctChoice)}. ${q.choices![q.correctChoice] ?? q.answer}`;
  }
  // A song can be any question in any round. Its key is title / artist.
  if (q.type === 'SONG' || round.roundType === 'AUDIO') {
    const combined = [q.songTitle, q.songArtist].filter(Boolean).join(' / ');
    return combined || q.answer;
  }
  if (round.twoAnswerMode && q.answerPartB) return `${q.answer} / ${q.answerPartB}`;
  return q.answer;
}
